// src/pages/CartPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext";
import { useNotification } from "../context/NotificationContext";
import { formatPrice } from "../utils/formatPrice";
import {
  getCartItems,
  removeCartItem,
  updateCartItemQuantity,
} from "../utils/cart";
import { 
  FiTrash2, 
  FiMinus, 
  FiPlus, 
  FiArrowLeft, 
  FiShield, 
  FiArrowRight, 
  FiShoppingBag, 
  FiCheckCircle 
} from "react-icons/fi";

const CartPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency, rates } = useCurrency();
  const { showNotification } = useNotification();
  const [items, setItems] = useState([]);

  useEffect(() => {
    const sync = () => setItems(getCartItems());
    sync();
    window.addEventListener("chafadi-cart-updated", sync);
    return () => window.removeEventListener("chafadi-cart-updated", sync);
  }, []);

  const total = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + Number(item.price || item.max_price || item.min_price || 0) * (item.quantity || 1),
        0
      ),
    [items]
  );

  const handleRemove = (id, name) => {
    removeCartItem(id);
    showNotification(t("notifications.productRemovedFromCart", { name }), "warning");
    setItems(getCartItems());
  };

  const handleQuantity = (id, quantity) => {
    updateCartItemQuantity(id, Math.max(1, quantity));
    setItems(getCartItems());
  };

  return (
    <div className="bg-[var(--color-bg)] min-h-screen font-sans text-[var(--color-text-primary)] pb-28 lg:pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 border-b border-[var(--color-border)] pb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-accent)]">
              <FiShoppingBag size={26} />
            </div>
            <div className="space-y-0.5">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.025em] text-[var(--color-text-primary)]">
                {t("cartPage.title")}
              </h1>
              <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] font-medium">
                {t("cartPage.itemsCount", { count: items.length })}
              </p>
            </div>
          </div>
          <Link
            to="/search/"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--color-accent)] hover:underline"
          >
            <FiArrowLeft size={15} /> {t("cartPage.continueBrowsing")}
          </Link>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 px-6 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-[24px] text-center animate-fade-in shadow-sm">
            <div className="w-20 h-20 rounded-full bg-[var(--color-bg)] flex items-center justify-center mb-6 border border-[var(--color-border)] text-[var(--color-accent)]">
              <FiShoppingBag size={32} />
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-[var(--color-text-primary)] mb-2">
              {t("cartPage.emptyCartTitle")}
            </h3>
            <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] mb-8 max-w-sm leading-relaxed">
              {t("cartPage.emptyCartText")}
            </p>
            <Link
              to="/search/"
              className="btn-primary h-12 px-8 text-[14px]"
            >
              {t("cartPage.startShopping")}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Items List */}
            <div className="lg:col-span-8 space-y-4">
              {items.map((item) => (
                <div 
                  key={item.id} 
                  className="group flex flex-col sm:flex-row items-center gap-6 p-5 bg-[var(--color-bg-secondary)] rounded-[18px] border border-[var(--color-border)] shadow-sm hover:shadow-md transition-all"
                >
                  {/* Item Image */}
                  <Link 
                    to={`/products/${item.id}`} 
                    className="shrink-0 w-24 h-24 rounded-2xl overflow-hidden bg-[var(--color-bg)] border border-[var(--color-border)] p-2 flex items-center justify-center"
                  >
                    <img
                      src={item.thumbnail_url || "https://via.placeholder.com/600x600.png?text=Chafadi"}
                      alt={item.name}
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </Link>

                  {/* Item Info */}
                  <div className="flex-1 min-w-0 text-center sm:text-left space-y-1">
                    <Link to={`/products/${item.id}`} className="no-underline block truncate">
                      <h3 className="text-[1.0625rem] font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-accent)] transition-colors">
                        {item.name}
                      </h3>
                    </Link>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <span className="text-[0.9375rem] font-semibold text-[var(--color-text-primary)]">
                        {formatPrice(item.price || item.max_price || item.min_price, currency, rates)}
                      </span>
                      {item.supplier && (
                        <div className="flex items-center gap-2 justify-center sm:justify-start text-[11px] text-[var(--color-text-secondary)]">
                          <span>•</span>
                          <span>{item.supplier.supplier_profile?.company_name || "Official Partner"}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex flex-col items-center sm:items-end gap-3 shrink-0">
                    <div className="flex items-center bg-[var(--color-bg)] border border-[var(--color-border)] rounded-full h-9 px-1">
                      <button 
                        onClick={() => handleQuantity(item.id, (item.quantity || 1) - 1)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-all"
                      >
                        <FiMinus size={13} />
                      </button>
                      <span className="w-8 text-center font-semibold text-[var(--color-text-primary)] text-xs">
                        {item.quantity || 1}
                      </span>
                      <button 
                        onClick={() => handleQuantity(item.id, (item.quantity || 1) + 1)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-all"
                      >
                        <FiPlus size={13} />
                      </button>
                    </div>
                    <button
                      className="text-[11px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-error)] transition-colors flex items-center gap-1.5"
                      onClick={() => handleRemove(item.id, item.name)}
                    >
                      <FiTrash2 size={13} />
                      {t("cartPage.removeFromCart")}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Sidebar Summary */}
            <div className="lg:col-span-4 lg:sticky lg:top-24">
              <div className="bg-[var(--color-bg-secondary)] rounded-[22px] border border-[var(--color-border)] p-6 shadow-sm space-y-6">
                <h2 className="text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t("cartPage.transactionOverview")}
                </h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-xs text-[var(--color-text-secondary)]">
                    <span>{t("cartPage.subtotal")}</span>
                    <span className="font-semibold text-[var(--color-text-primary)]">{formatPrice(total, currency, rates)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-[var(--color-text-secondary)]">
                    <span>{t("cartPage.networkFee")}</span>
                    <span className="font-semibold text-[var(--color-success)]">{t("cartPage.included")}</span>
                  </div>
                </div>
                
                <div className="pt-5 border-t border-[var(--color-border)] space-y-5">
                  <div className="flex justify-between items-baseline">
                    <p className="text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                      {t("cartPage.total")}
                    </p>
                    <span className="text-2xl sm:text-3xl font-semibold text-[var(--color-text-primary)] tracking-tight">
                      {formatPrice(total, currency, rates)}
                    </span>
                  </div>

                  <button
                    className="btn-primary w-full h-12 text-[14px] gap-2 shadow-sm"
                    onClick={() => navigate("/checkout")}
                  >
                    {t("cartPage.lockIn")}
                    <FiArrowRight size={15} />
                  </button>
                  
                  <div className="flex items-center justify-center gap-2 py-2.5 bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)]">
                    <FiShield className="text-[var(--color-accent)]" size={14} />
                    <span className="text-[10px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                      SECURED CHECKOUT • NIGER
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 px-4 py-3 flex items-center gap-3 opacity-80">
                <FiCheckCircle size={17} className="text-[var(--color-accent)] shrink-0" />
                <p className="text-[11px] text-[var(--color-text-secondary)] leading-relaxed">
                  {t("cartPage.bulkDiscountNote")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Mobile Checkout Bar (DESIGN.md §6) */}
      {items.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[990] bg-[var(--glass-bg)] backdrop-blur-2xl border-t border-[var(--color-border)] px-4 py-3 pb-[max(env(safe-area-inset-bottom),12px)] flex items-center justify-between gap-4 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]">
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] font-semibold text-[var(--color-text-secondary)] uppercase">
              {t("cartPage.total")}
            </span>
            <span className="text-lg font-semibold text-[var(--color-text-primary)] truncate">
              {formatPrice(total, currency, rates)}
            </span>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            className="btn-primary h-11 px-6 text-[13px] gap-1.5 whitespace-nowrap shadow-sm"
          >
            {t("cartPage.lockIn")} <FiArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
